import React, { useEffect, useState } from "react";
import { ArrowLeftOutlined, DownloadOutlined, EditOutlined, InfoCircleOutlined, LoadingOutlined, LinkOutlined, UndoOutlined, QuestionOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Spin, Row, Col, Button, Tooltip, message } from "antd";
import CustomerMainLayout from "../CustomerComponents/CustomerMainLayout";
import ENVIRONMENT from "../../../environments";
import "./EditAiImage.scss";
import { Header } from 'antd/lib/layout/layout';
import '../CustomerComponents/AIEditorModalViewer/AIEditorModalViewer.scss';
import HighResModal from "../CustomerComponents/AIEditorModalViewer/HighResModal";
import TextEditor from "../CustomerComponents/AIEditorModalViewer/TextEditor";
import ShareModal from "../CustomerComponents/AIEditorModalViewer/ShareModal";
import axios from 'axios';
import { downloadFile, copyToClipboard, setCookie, getCookie, getDisplayEngineName, getEngineLogo } from "../Utilities";
import SubscriptionUpgradeManager from "../CustomerComponents/SubscriptionUpgradeManager/SubscriptionUpgradeManager";
import CompareSiloView from "../CustomerComponents/AIEditorModalViewer/CompareSiloView";
import RejectionHistoryPageDrawer from "../CustomerComponents/ReviewImagesPage/RejectionHistoryPageDrawer";
import pako from "pako";

let isAiCustomer = localStorage.getItem('is_ai_customer') === 'true';
const EditAiImage = () => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [showHighResModal, setShowHighResModal] = useState(false);
  const [showEditPromptModal, setShowEditPromptModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [savePromptLoader, setSavePromptLoader] = useState(false);
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [productId, setProductId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageRequestStatus, setImageRequestStatus] = useState(
    'update_subscription'
  );
  const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false)
  const [generateResponse, setGenerateResponse] = useState(null);
  const [cardStatus, setCardStatus] = useState(0);
  const [compareSiloModalOpen, setCompareSiloModalOpen] = useState(false);
  const [compareImageData, setCompareImageData] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [data, setData] = useState(null);
  const [rejectionHistoryVisible, setRejectionHistoryVisible] = useState(false);
  const [rejectionHistory, setRejectionHistory] = useState([]);
  const [propsLibrary, setPropsLibrary] = useState([]);
  const [hideHeader, setHideHeader] = useState(false);
  let urlParams = new URLSearchParams(window.location.search);

  const launchAiEditorHelpTour = () => {
    if (window.Storylane) {
      window.Storylane.Play({
        type: 'popup',
        demo_type: 'image',
        width: 2560,
        height: 1442,
        scale: '0.95',
        demo_url: `https://app.storylane.io/demo/${ENVIRONMENT.AI_EDITOR_STORYLANE_ID}`,
        padding_bottom: 'calc(56.33% + 27px)'
      });
      setCookie('ai_editor_tour_viewed', 'true', 365);
      return true;
    }
    return false;
  };

  const loadStorylineScript = () => {
    const script = document.createElement('script');
    script.src = 'https://js.storylane.io/js/v1/storylane.js';
    script.async = true;

    // Auto-open tour for first-time viewers after script loads
    script.onload = () => {
      const hasViewedTour = getCookie('ai_editor_tour_viewed');
      if (!hasViewedTour) {
        setTimeout(() => {
          launchAiEditorHelpTour();
        }, 1500);
      }
    };

    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  };

  const fetchRejectionHistory = (imageId) => {
    if (!imageId) return;

    axios.post(ENVIRONMENT.AI_IMAGE_GET_REJECTIONS, {
      image_id: imageId
    })
      .then(res => {
        if (res.data) {
          setRejectionHistory(res.data);
        }
      })
      .catch(err => {
        console.error('Error fetching rejection history:', err);
      });
  };

  const fetchPropsLibrary = () => {
    try {
      const username = localStorage.getItem("username");
      const companyId = localStorage.getItem("company_id");
      if (!username || !companyId) return;

      const propsPayload = {
        action: "get_all_props",
        username,
        company_id: parseInt(companyId, 10)
      };

      axios.post(ENVIRONMENT.AI_ASSETS_HELPER, propsPayload)
        .then(res => {
          const plib = res?.data;
          let libraryData = Array.isArray(plib) ? plib : (plib?.data || plib?.items || []);

          // Add base URL to image URIs
          const baseURL = platform ? ENVIRONMENT.getBaseURL(platform) : ENVIRONMENT.getBaseURL();
          libraryData = libraryData.map(item => ({
            ...item,
            image_uri: item.image_uri && !item.image_uri.startsWith('http')
              ? baseURL + item.image_uri
              : item.image_uri,
            thumbnail: item.thumbnail && !item.thumbnail.startsWith('http')
              ? baseURL + item.thumbnail
              : item.thumbnail
          }));

          setPropsLibrary(libraryData);
          console.log('Props library fetched:', libraryData);

          // Send to iframe
          sendPropsLibraryToIframe(libraryData);
        })
        .catch(err => {
          console.error('Error fetching props library:', err);
        });
    } catch (e) {
      console.error('Error in fetchPropsLibrary:', e);
    }
  };

  const sendPropsLibraryToIframe = (libraryData) => {
    const iframe = document.querySelector('.edit-iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'PROPS_LIBRARY_DATA',
        data: { propsLibrary: libraryData || propsLibrary, platform: platform, product_id: productId },
      }, ENVIRONMENT.AI_IMAGE_EDITOR_BASE_URL);
    }
  };

  const getImages = (shouldUpdateSelectedImage = true) => {
    let productId = urlParams.get('productId');
    let imageId = urlParams.get('imageId');
    let promptText = urlParams.get('promptText');
    let imageUrl = urlParams.get('imageUrl');
    setImageUrl(imageUrl);
    setPromptText(promptText);
    setProductId(productId);
    setImagesLoading(true);
    let payload = {
      action: 'get_ai_assets',
      product_id: productId,
      username: localStorage.getItem('username'),
      company_id: localStorage.getItem('company_id'),
    }

    return axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload)
      .then(res => {
        console.log(res.data);
        setData(res.data);
        setImages(res?.data?.lifestyle_renders || []);
        setPlatform(res?.data?.platform);
        // Find the specific image object by imageId
        if (shouldUpdateSelectedImage && res.data.lifestyle_renders && imageId) {
          const selectedImage = res.data.lifestyle_renders.find(image =>
            image.id.toString() === imageId.toString()
          );
          if (selectedImage) {
            console.log('Found selected image:', selectedImage);
            // Update imageData with the found image object
            setImageData({
              ...selectedImage,
              imageUrl: ENVIRONMENT.getBaseURL(res?.data?.platform) + selectedImage.uri, // Update with the actual URI from the response
              displayName: selectedImage.display_name || 'Untitled',
              id: selectedImage.render_id || selectedImage.id,
            });

            fetchRejectionHistory(imageId);
          } else {
            console.warn('Image with ID', imageId, 'not found in lifestyle_renders');
          }
        }
        setImagesLoading(false);
        return res;
      })
      .catch(err => {
        console.error('Error getting image data:', err);
        setImagesLoading(false);
      });
  }

  useEffect(() => {
    setIframeLoading(true);
    getImages();
    loadUserRequestStatus();
    fetchPropsLibrary(); // Fetch props library on mount
    const cleanupScript = loadStorylineScript();
    return cleanupScript;
  }, []);

  useEffect(() => {
    // Listen for messages from iframe
    const handleMessage = async (event) => {
      const findImage = (list, id) => list.find(image =>
        (image.id && image.id.toString() === id.toString()) ||
        (image.render_id && image.render_id.toString() === id.toString())
      );

      if (event.data.type === 'IMAGE_SELECTED') {
        const { imageId, imageUrl, displayName } = event.data.payload;

        let selectedImage = findImage(images, imageId);

        if (!selectedImage) {
          console.log("selectedImage not found, refetching...")
          const res = await getImages(false);
          const freshImages = res?.data?.lifestyle_renders || [];
          selectedImage = findImage(freshImages, imageId);
        }
        setImageData({
          imageUrl: imageUrl,
          displayName: displayName,
          id: imageId,
          engine: selectedImage?.engine,
        });
      } else if (event.data.type == 'GO_TO_AI_CREATOR') {
        goToGenerateMore();
      } else if (event.data.type == 'AI_GENERATE_REQUEST') {
        console.log('event.data.payload here', event.data);
        if (imageRequestStatus == "allowed" || imageRequestStatus == "always_allowed") {
          let gateway_payload = {
            "username": localStorage.getItem('username'),
            "request_type": "ai_image_1k",
            "action": "perform",
            "request_payload": event.data.payload,
            "category": "",
            "request_id": Date.now().toString()
          }
          axios.post(ENVIRONMENT.PAYMENT_GATEWAY, gateway_payload).then((res) => {
            console.log(res.data);
            setGenerateResponse(res.data);

            // Notify other tabs that an AI image edit has been submitted
            try {
              const bc = new BroadcastChannel('ai_image_edit_channel');
              bc.postMessage({ type: 'AI_IMAGE_EDIT_SUBMITTED', payload: { productId, imageId: imageData?.id } });
              bc.close();
            } catch (e) {
              console.warn('BroadcastChannel not supported:', e);
            }
          });
        } else {
          setSubscriptionUpgradeManagerModel(true);
        }
      } else if (event.data.type === 'COMPARE_IMAGE') {
        const { imageId } = event.data.payload;
        if (imageId && images.length > 0) {
          // Find the image by ID
          let selectedImage = findImage(images, imageId);

          if (selectedImage) {
            const baseUrl = platform ? ENVIRONMENT.getBaseURL(platform) : ENVIRONMENT.getBaseURL();
            const compareData = {
              ...selectedImage,
              selectedImageUrl: baseUrl + selectedImage.uri,
              input_image_uri: data?.input_image_uri || selectedImage?.input_image_uri,
              inspiration_image_uri: selectedImage?.inspiration_image_uri,
              platform: platform,
              product_id: productId,
              processed_silo: selectedImage?.processed_silo,
              width: data?.width,
              height: data?.height,
              depth: data?.depth,
              render_props: selectedImage.props?.filter(item => item.product !== true)
            };
            setCompareImageData(compareData);
            setCompareSiloModalOpen(true);
          } else {
            console.warn('Image with ID', imageId, 'not found in images array');
            message.warning('Image not found for comparison');
          }
        } else {
          console.warn('Image ID not provided or images not loaded yet');
          message.warning('Please wait for images to load');
        }
      } else if (event.data.type === 'REFRESH_PROPS_LIBRARY') {
        console.log('Received refresh props library request from iframe');
        fetchPropsLibrary();
      } else if (event.data.type === 'SMART_PATCH_CLICKED') {
        console.log('Received SMART_PATCH_CLICKED from iframe', event.data.payload);
        setHideHeader(true);
      } else if (event.data.type === 'SMART_PATCH_CLOSED') {
        console.log('Received SMART_PATCH_CLOSED from iframe');
        setHideHeader(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup event listener
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [images, imageRequestStatus, platform, productId, imageUrl, data, imageData]);


  const loadUserRequestStatus = () => {
    // Check AI image request status
    let imagePayload = {
      "username": localStorage.getItem('username'),
      "request_type": "ai_image_1k",
      "action": "check",
      "category": ""
    }
    axios.post(ENVIRONMENT.PAYMENT_GATEWAY, imagePayload)
      .then(res => {
        console.log('Image request status:', res.data)
        setImageRequestStatus(res.data['request_allowed']);
        setCardStatus(res.data['card_status']);
      });
  }


  const handleIframeLoad = () => {
    setIframeLoading(false);
    // Don't send data here, wait for imageData to be available
  };

  // Send data to iframe whenever imageData changes
  const sendDataToIframe = () => {
    if (imageData) {
      const iframe = document.querySelector('.edit-iframe');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'AI_IMAGE_DATA',
          data: {
            ...imageData,
            username: localStorage.getItem('username'),
            company_id: localStorage.getItem('company_id'),
            product_id: productId,
            platform: platform,
          },
        }, ENVIRONMENT.AI_IMAGE_EDITOR_BASE_URL);
      }
    }
  };

  // Effect to send data to iframe when imageData is available
  useEffect(() => {
    if (imageData && !iframeLoading) {
      // Small delay to ensure iframe is ready
      const timer = setTimeout(() => {
        sendDataToIframe();
        // Also send props library
        sendPropsLibraryToIframe();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [imageData, iframeLoading]);

  // Effect to send generateResponse to iframe when it changes
  useEffect(() => {
    if (generateResponse && !iframeLoading) {
      const iframe = document.querySelector('.edit-iframe');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'AI_GENERATE_RESPONSE',
          data: { ...generateResponse, platform: platform, product_id: productId },
        }, ENVIRONMENT.AI_IMAGE_EDITOR_BASE_URL);
        setGenerateResponse(null);
      }
    }
  }, [generateResponse, iframeLoading]);

  // Effect to send cancellation message when subscription modal is shown
  useEffect(() => {
    if (subscriptionUpgradeManagerModel && !iframeLoading) {
      const iframe = document.querySelector('.edit-iframe');
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'AI_GENERATE_REQUEST_CANCELLED',
          data: { reason: 'subscription_required', platform: platform, product_id: productId },
        }, ENVIRONMENT.AI_IMAGE_EDITOR_BASE_URL);
      }
    }
  }, [subscriptionUpgradeManagerModel, iframeLoading]);

  const handleIframeError = () => {
    setIframeLoading(false);
    setIframeError(true);
  };

  const goToGenerateMore = () => {
    const toBase64Url = (uint8) => {
      let binary = '';
      for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };
    const compressState = (obj) => {
      const json = JSON.stringify(obj || {});
      const deflated = pako.deflate(json);
      return toBase64Url(deflated);
    };

    const params = new URLSearchParams();
    params.set("stage", "lifestyle_generation");
    params.set("productId", productId || "");
    params.set("aiModel", "true");

    // Prefer canonical job_id when available (avoid using render_id/imageId).
    const jobId = imageData?.job_id || data?.job_id || data?.latest_job_id || "";
    if (jobId) params.set("job_id", String(jobId));

    const state = {
      img: data?.input_image_uri || imageData?.input_image_uri || imageUrl || "",
      imgInspection: data?.inspiration_image_uri || imageData?.inspiration_image_uri || "",
      inspiration_id: data?.inspiration_id || imageData?.inspiration_id || null,
      // Prefer job-level prompt when available; fallback to query promptText.
      prompt: data?.prompt_text || promptText || "",
      props: data?.props || imageData?.props || [],
      aspect_ratio: data?.latest_aspect_ratio || imageData?.latest_aspect_ratio || "1:1",
    };

    params.set("state", compressState(state));
    window.location.href = `/ai-asset-creator?${params.toString()}`;
  };

  const handleBackBtn = () => {
    window.history.back();
  };

  const downloadSelectedImage = () => {
    if (imageData?.imageUrl) {
      // Extract extension from URL
      const url = imageData.imageUrl;
      const urlWithoutParams = url.split('?')[0]; // Remove query params
      const extension = urlWithoutParams.substring(urlWithoutParams.lastIndexOf('.') + 1);
      downloadFile(imageData.imageUrl, imageData.displayName, extension);
    }
  };

  const handleCloseEditPrompt = () => {
    setShowEditPromptModal(false);
  };

  const handleSavePrompt = (editedText) => {
    setSavePromptLoader(true);
    console.log('Saving prompt:', editedText);
    console.log('Image data:', imageData);
    let payload = {
      id: `${imageData.id}`,
      display_name: editedText,
      action: 'edit_ai_image_name'
    }
    console.log(payload)
    axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload)
      .then(res => {
        setSavePromptLoader(false);
        setImageData(prevData => ({
          ...prevData,
          displayName: editedText
        }));
        setShowEditPromptModal(false);
        message.success('Name changed successfully!');
      })
  };

  const handleHighResModalClose = () => {
    setShowHighResModal(false);
  };

  const handleAiEditorTour = () => {
    const success = launchAiEditorHelpTour();
    if (!success) {
      message.warning('Tour is loading, please try again in a moment');
    }
  };

  const closeCompareSiloModal = () => {
    setCompareSiloModalOpen(false);
  };

  const closeAllModals = () => {
    setCompareSiloModalOpen(false);
    setShowHighResModal(false);
    setShowEditPromptModal(false);
  };

  const closeRejectionHistoryDrawer = () => {
    setRejectionHistoryVisible(false);
  };

  return (
    <CustomerMainLayout selected="26" remove_global_padding={true} hide_scroll={true}>
      {!hideHeader && (
        <Header className='product-nav-header white'>
          <Row className='display-flex j-s-b a-c'>
            <Col span={16}>
              <div className='display-flex j-s a-c ai-editor-left-header'>
                <div className='manrope f-14 lh-28 black-73 white-space-text cursor-pointer mr-24'
                  onClick={handleBackBtn}>
                  <ArrowLeftOutlined className='mr-8' />
                  Back
                </div>

                <div className='ai-gradient-container ml-16'>
                  <img src="/img/ai_creator/ai-twinkle-high-res.svg" />
                  <span className='manrope f-14 w-600 lh-28 ai-gradient ml-4'>AI</span>
                </div>
                <div className='ai-editor-title-block ml-16'>
                  <div className='display-flex j-s a-c'>
                    <Tooltip title={<span className="manrope f-12 white">{imageData?.displayName}</span>}>
                      <span className="manrope f-20 lh-28 w-500 black-085 ai-prompt-text">
                        {imageData?.displayName || 'Untitled'}
                      </span>
                    </Tooltip>
                    <Tooltip title={<span className="manrope f-12 white">Edit Name</span>}>
                      <EditOutlined
                        className='ml-8 manrope blue cursor-pointer'
                        onClick={() => setShowEditPromptModal(true)}
                      />
                    </Tooltip>
                  </div>
                  <div className='ai-editor-warning-under-title'>
                    <span className='manrope ai-editor-warning-text'>
                      AI can make mistakes, so double-check it and retry
                    </span>
                  </div>
                </div>
              </div>
            </Col>
            <Col span={8} className='display-flex j-e a-c'>

              <div className='ai-editor-warning-wrap'>
                <span className='manrope ai-editor-warning-text'>
                  AI can make mistakes, so double-check it and retry
                </span>
              </div>

              <Tooltip title={<span className="manrope f-12 white">Help</span>}>
                <Button className='manrope f-14 w-500 lh-22 ai-action-btn' onClick={handleAiEditorTour}>
                  <QuestionOutlined style={{ fontSize: '18px' }} />
                </Button>
              </Tooltip>

              <Tooltip title={<span className="manrope f-12 white">Download Preview</span>}>
                <Button className='manrope f-14 w-500 lh-22 ai-action-btn ml-8' onClick={downloadSelectedImage}>
                  <DownloadOutlined style={{ fontSize: '18px' }} />
                </Button>
              </Tooltip>

              <Tooltip title={<span className="manrope f-12 white">Share Image</span>}>
                <Button
                  className='manrope f-14 w-500 lh-22 ai-action-btn ml-8'
                  onClick={() => setShowShareModal(true)}
                  disabled={!imageData?.imageUrl}
                >
                  <LinkOutlined style={{ fontSize: '18px' }} />
                </Button>
              </Tooltip>

              {rejectionHistory && rejectionHistory.length > 0 && (
                <Tooltip title={<span className='manrope f-12 white'>Rejection History</span>}>
                  <Button
                    className='manrope f-14 w-500 lh-22 ai-action-btn ml-8'
                    onClick={() => setRejectionHistoryVisible(true)}
                  >
                    <ExclamationCircleOutlined style={{ fontSize: '18px' }} />
                  </Button>
                </Tooltip>
              )}

              <Button className='manrope f-14 w-500 lh-22 ai-action-btn ml-8' onClick={goToGenerateMore}>
                <UndoOutlined style={{ fontSize: '16px', transform: 'scaleX(-1)' }} />
              </Button>

              <div className='display-flex a-c ml-16'>
                <Button className='manrope f-14 w-500 lh-22 ai-action-btn-blue' onClick={() => setShowHighResModal(true)}>
                  Save As Final
                </Button>
              </div>
            </Col>
          </Row>
        </Header>
      )}
      <div className={`edit-ai-image-container`}>
        {(iframeLoading || imagesLoading) && (
          <div className="iframe-loader">
            <LoadingOutlined className="manrope f-46 w-700" style={{ color: '#808080' }} spin />
          </div>
        )}

        {iframeError && (
          <div className="iframe-error">
            <h3>Failed to load editor</h3>
            <p>Please try refreshing the page or contact support if the problem persists.</p>
            <button onClick={() => window.location.reload()}>
              Refresh Page
            </button>
          </div>
        )}

        <iframe
          src={ENVIRONMENT.AI_IMAGE_EDITOR_BASE_URL}
          className="edit-iframe"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>
      <CompareSiloView
        isOpen={compareSiloModalOpen}
        onClose={closeCompareSiloModal}
        closeAllModals={closeAllModals}
        imageData={compareImageData}
      />
      <HighResModal
        isOpen={showHighResModal}
        onClose={handleHighResModalClose}
        imageData={imageData}
      />
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        imageData={
          imageData
            ? {
              ...imageData,
              selectedImageUrl: imageData.imageUrl,
              display_name: imageData.displayName || 'Untitled',
              render_id: imageData.id,
            }
            : null
        }
      />
      <TextEditor
        isOpen={showEditPromptModal}
        onClose={handleCloseEditPrompt}
        initialText={imageData?.displayName || 'Untitled'}
        onSave={handleSavePrompt}
        placeholder="Enter image name..."
        rows={4}
        loader={savePromptLoader}
      />
      <RejectionHistoryPageDrawer
        visible={rejectionHistoryVisible}
        onClose={closeRejectionHistoryDrawer}
        rejections={rejectionHistory}
        imageTitle={imageData?.displayName || 'Untitled'}
        createdBy={imageData?.username || ''}
        createdOn={imageData?.submitted_on || ''}
        collabId={''}
        promptText={imageData?.prompt_text}
        productId={productId}
        inspirationImageUri={imageData?.inspiration_image_uri}
      />
      <SubscriptionUpgradeManager
        subscriptionUpgradeManagerModel={subscriptionUpgradeManagerModel}
        setSubscriptionUpgradeManagerModel={setSubscriptionUpgradeManagerModel}
        cardStatus={cardStatus}
        loadUserRequestStatus={loadUserRequestStatus}
        requestType={'ai_image_1k'}
      />
    </CustomerMainLayout>
  );
};

export default EditAiImage;
